import { useEffect, useState } from "react";
import DashboardLayout from "../../../layoutcontainers/DashboardLayout";
import Table from "../../../components/Table";
import MaterialTypography from "../../../components/MaterialTypography";
import { Card, CardHeader, Stack } from "@mui/material";
import { useNavigate } from "react-router-dom";
import MaterialButton from "../../../components/MaterialButton";
import AddIcon from "@mui/icons-material/Add";
import { FileUpload, FilterAlt } from "@mui/icons-material";
import { deleteData, fetchData } from "../../../services";
import { useDispatch, useSelector } from "react-redux";
import {
  setCurrentPage,
  setRowsPerPage,
  setTotalPages,
} from "./store/motorBodyTypeStore";
import BulkUpload from "../../../components/blukUplod";
import { useLoading } from "../../../components/Loading";
import BodyTypeListFilter from "./components/BodyTypeListFilter.js";
import { useSnackbar } from "../../../context/popup.js";

const MotorBodyTypeList = () => {

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { setCircleLoading } = useLoading();
  const { showSnackbar } = useSnackbar();

  const currentPage =
    useSelector((state) => state?.motorBodyTypeStore?.currentPage) || 1;
  const rowsPerPage =
    useSelector((state) => state?.motorBodyTypeStore?.rowsPerPage) || 10;
  const totalPages =
    useSelector((state) => state?.motorBodyTypeStore?.totalPages) || 1;

  const [openUploadFileDialog, setOpenUploadFileDialog] = useState(false);
  const [isRefresh, setIsRefresh] = useState(false);
  const [bodyTypeData, setBodyTypeData] = useState([]);
  const [search, setSearch] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const [filterData, setFilterData] = useState({
    isActive: "",
  });

  const fetchBodyTypeData = async () => {
    setCircleLoading(true);
    try {
      let url = `/reference/motor/body-type?page=${currentPage}&limit=${rowsPerPage}&searchTerm=${search}`;

      //for active and inactive status
      if (filterData.isActive !== "") {
        url += `&isActive=${filterData.isActive}`;
      }

      const response = await fetchData(url);
      if (response?.success) {
        setBodyTypeData(response?.data?.items);
        dispatch(setTotalPages(response?.data?.totalPages));
      } else {
        showSnackbar("Error Fetching Body Type Data", "error");
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      showSnackbar(error?.response?.data?.message || 'Network Error', "error");
    } finally {
      setCircleLoading(false);
    }
  };

  useEffect(() => {
    fetchBodyTypeData();
  }, [isRefresh, currentPage, rowsPerPage, search]);

  const columns = [
    {
      key: "sno",
      label: "S.No",
      renderCell: (row) => row.sno,
    },
    {
      key: "name",
      label: "Body Type",
      renderCell: (row) => row.name,
    },
    {
      key: "createdAt",
      label: "Created At",
      renderCell: (row) => row.createdAt,
    },
    {
      key: "active",
      label: "Status",
      renderCell: (row) => (
        <span>
          {row.isActive ? (
            <span style={{ color: "green", fontWeight: "bold" }}>Active</span>
          ) : (
            <span style={{ color: "red", fontWeight: "bold" }}>Inactive</span>
          )}
        </span>
      ),
    },
  ];

  const rows = bodyTypeData.map((bodyType, index) => ({
    ...bodyType,
    sno: index + 1,
    createdAt: new Date(bodyType.createdAt).toLocaleDateString("en-GB"),
  }));

  const handlePaginationChange = (page) => {
    dispatch(setCurrentPage(page));
  };

  const handleRowsPerPageChange = (rowsPerPage) => {
    dispatch(setRowsPerPage(rowsPerPage));
    dispatch(setCurrentPage(1));
  };

  const handleEdit = (row) => {
    navigate(`/motor-master/edit-bodyType/${row?.id}`);
  };

  const handleView = (row) => {
    navigate(`/motor-master/view-bodyType/${row?.id}`);
  };

  const handleDelete = async (row) => {
    try {
      const response = await deleteData(`/motor/body-type-delete`, {
        ids: [row?.id],
      });
      setIsRefresh(!isRefresh);
      const responseMessage = response?.message;
      if (response?.success) {
        showSnackbar(responseMessage,"success");
      } else {
        showSnackbar(responseMessage,"error");
      }
    } catch (error) {
      console.error("Error deleting data:", error);
      showSnackbar(error?.response?.data?.message || "Error deleting data", "error");
    }
  };

  const actions = {
    edit: true,
    view: true,
    delete: true,
    onEdit: (row) => handleEdit(row),
    onView: (row) => handleView(row),
    onDelete: (row) => handleDelete(row),
  };

  const handleAddNewBodyType = () => {
    navigate("/motor-master/add-bodyType");
  };

  const handleSearch = (value) => {
    setSearch(value);
  };
  return (
    <DashboardLayout>
      <CardHeader
        title={
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            spacing={2}
          >
            <Stack direction="column" spacing={0}>
              <MaterialTypography variant="h5" fontWeight="medium">
                Motor Body Type management list
              </MaterialTypography>
              <MaterialTypography variant="caption" color="text">
                List of motor body type that you can get details of motor body
                type.
              </MaterialTypography>
            </Stack>

            <Stack direction="row" spacing={1}>
              <MaterialButton
                variant="gradient"
                color="dark"
                size="small"
                startIcon={<FilterAlt />}
                onClick={(event) => setAnchorEl(event.currentTarget)}
              >
                Filter
              </MaterialButton>
              <MaterialButton
                variant="gradient"
                color="dark"
                size="small"
                startIcon={<FileUpload />}
                onClick={() => setOpenUploadFileDialog(true)}
              >
                Bulk Upload
              </MaterialButton>
              <MaterialButton
                variant="gradient"
                color="dark"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => handleAddNewBodyType()}
              >
                Add Body Type
              </MaterialButton>
            </Stack>
          </Stack>
        }
      />
      <Card sx={{ mx: 1 }}>
        <Table
          columns={columns}
          data={rows}
          actions={actions}
          pagination={{
            totalPages,
            currentPage,
            onPageChange: (page) => handlePaginationChange(page),
            rowsPerPage,
            onRowsPerPageChange: (rowsPerPage) =>
              handleRowsPerPageChange(rowsPerPage),
            rowsPerPageOptions: [5, 10, 15, 20],
          }}
          getRowKey={(row) => row.id}
          className="custom-table"
          noDataLabel={"No Body Type Data Found"}
          handleSearch={handleSearch}
        />
      </Card>
      <BulkUpload
        open={openUploadFileDialog}
        setOpen={setOpenUploadFileDialog}
        endPoint="/reference/master/bulkUpload/body-types"
        fetchList={fetchBodyTypeData}
      />
      <BodyTypeListFilter
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        filterData={filterData}
        setFilterData={setFilterData}
        onApply={() => {
          setAnchorEl(null); // close popup
          fetchBodyTypeData(); // call API after filter applied
        }}
      />
    </DashboardLayout>
  );
};

export default MotorBodyTypeList;
